import { Spinner, Alert } from "react-bootstrap";
import { JourneyScope, useConsumerJourney } from "./useConsumerJourney";
import { ConsumerJourneyStepNode } from "@models/population.model";

/* Addressed by campaign (launchId) from the population panel, or by population
   (populationSegmentId) from the data-file roster (MPN-1602). */
type PopulationRouteViewProps = JourneyScope & {
  consumerId: number;
};

const DOT_SIZE = 12;
const GUTTER_WIDTH = 28;
const LINE_COLOR = "#c7c9d1";

const CONVERSION_COLOR = "#34c38f";
const REVERSION_COLOR = "#f46a6a";
const PRECONVERT_COLOR = "#50a5f1";
const START_COLOR = "#adb5bd";

// as_of is a date-only string (YYYY-MM-DD). Parse the parts as a local date so the
// displayed day doesn't shift across timezones (new Date("2026-03-31") is UTC midnight).
function formatStepDate(iso: string | null): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function isStartingStep(step: ConsumerJourneyStepNode): boolean {
  // The "entered here" baseline is the price-sheet-item seq-0 trace (is_start), NOT
  // the journey-step sequence — a real conversion step can itself be at step-seq 0.
  return step.is_start;
}

// Event label + dot color. The starting step (sequence 0) is the consumer's "entered here"
// baseline — backend emits this as timeline kind "start". It is NOT a conversion; it gets a
// distinct neutral "Start · entered here" marker alongside its own name (e.g. "Stopped Out").
function eventOf(step: ConsumerJourneyStepNode): { label: string; color: string; textClass: string } {
  if (isStartingStep(step)) return { label: "Start", color: START_COLOR, textClass: "text-muted" };
  if (step.is_preconvert) return { label: "Pre-convert", color: PRECONVERT_COLOR, textClass: "text-info-emphasis" };
  if (step.is_reversion) return { label: "Reversion", color: REVERSION_COLOR, textClass: "text-danger" };
  return { label: "Conversion", color: CONVERSION_COLOR, textClass: "text-success" };
}

export const PopulationRouteView = ({ consumerId, ...scope }: PopulationRouteViewProps) => {
  const { data, loading, error } = useConsumerJourney({ enabled: true, consumerId, ...scope });

  // Hide the synthetic "starting step" (sequence 0 / "Start" baseline). It's a made-up
  // entry marker, not a real event, and it crowds the real conversions below the fold.
  // Show only real journey events, chronologically. (Drop this filter once step-0 is
  // removed at the source.)
  const steps = data
    ? data.routes
        .flatMap((r) => r.steps)
        .filter((s) => !isStartingStep(s))
        .slice()
        .sort((a, b) => (a.as_of ?? "").localeCompare(b.as_of ?? ""))
    : [];

  return (
    <div className="border-top bg-light px-3 py-3">
      {loading && (
        <div className="d-flex justify-content-center p-3">
          <Spinner animation="border" size="sm" role="status" />
        </div>
      )}

      {error && !loading && <Alert variant="danger" className="mb-0">{error}</Alert>}

      {data && !loading && !error && (
        steps.length === 0 ? (
          <div className="text-muted small">No conversion activity recorded yet.</div>
        ) : (
          <div>
            {steps.map((s, i) => {
              const isLast = i === steps.length - 1;
              const ev = eventOf(s);
              return (
                <div key={s.trace_id} className="d-flex" style={{ minHeight: 52 }}>
                  <div style={{ position: "relative", width: GUTTER_WIDTH, flexShrink: 0 }}>
                    <div
                      style={{
                        width: DOT_SIZE,
                        height: DOT_SIZE,
                        borderRadius: "50%",
                        backgroundColor: ev.color,
                        border: "2px solid #ffffff",
                        boxShadow: `0 0 0 2px ${ev.color}`,
                        position: "absolute",
                        top: 5,
                        left: (GUTTER_WIDTH - DOT_SIZE) / 2,
                      }}
                    />
                    {!isLast && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: 5 + DOT_SIZE,
                            bottom: 2,
                            left: GUTTER_WIDTH / 2 - 1,
                            width: 2,
                            backgroundColor: LINE_COLOR,
                          }}
                        />
                        <i
                          className="bi bi-caret-down-fill"
                          style={{
                            position: "absolute",
                            bottom: -4,
                            left: GUTTER_WIDTH / 2 - 6,
                            color: LINE_COLOR,
                            fontSize: 12,
                            lineHeight: 1,
                          }}
                        />
                      </>
                    )}
                  </div>
                  <div
                    className="ps-2 pb-3 w-100"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) 7rem 10rem",
                      columnGap: "0.75rem",
                      alignItems: "baseline",
                    }}
                  >
                    <span className="fw-semibold text-truncate">
                      {s.name}
                      {s.journey_step_dimension_name && (
                        <span className="fw-normal text-muted"> · {s.journey_step_dimension_name}</span>
                      )}
                    </span>
                    <span className={ev.textClass}>{ev.label}</span>
                    <span className="text-muted small">{formatStepDate(s.as_of)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};
